import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { segmentApi, Segment, SegmentRQ } from '../../utils/segmentApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  useTheme,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Delete as DeleteIcon, Edit as EditIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import DataTable, { Column } from './DataTable';
import { useTranslation } from 'react-i18next';

function SegmentsList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [filtered, setFiltered] = useState<Segment[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const rs = await segmentApi.list();
      setSegments(rs);
      setFiltered(rs);
    } catch (e: any) {
      toast.error(e?.message || t('segments.errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const txt = searchText.trim().toLowerCase();
    if (!txt) {
      setFiltered(segments);
      return;
    }
    setFiltered(
      segments.filter(
        (s) =>
          s.name.toLowerCase().includes(txt) ||
          (s.description?.toLowerCase().includes(txt) ?? false)
      )
    );
  }, [searchText, segments]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = (a as any)[orderBy] ?? '';
      const bv = (b as any)[orderBy] ?? '';
      if (typeof av === 'string' && typeof bv === 'string') {
        return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return 0;
    });
    return arr;
  }, [filtered, orderBy, order]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('segments.removeConfirm'))) return;
    try {
      await segmentApi.remove(id);
      toast.success(t('segments.notifications.removed'));
      await load();
    } catch (e: any) {
      toast.error(e?.message || t('segments.errors.removeError'));
    }
  };

  const columns: Column<Segment>[] = [
    { id: 'name', label: t('segments.name'), minWidth: 160 },
    { id: 'description', label: t('segments.description'), minWidth: 280 },
    {
      id: 'actions',
      label: t('segments.actions'),
      minWidth: 100,
      format: (_v, row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-center' }}>
          <Tooltip title={t('segments.editTooltip')}>
            <IconButton size="small" onClick={() => navigate(`${row.id}/edit`)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('segments.deleteTooltip')}>
            <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {t('segments.manage')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          onClick={() => navigate('create')}
        >
          {t('segments.newSegment')}
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder={t('segments.searchPlaceholder')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {filtered.length} {filtered.length === 1 ? t('segments.segmentFound') : t('segments.segmentsFound')}
        </Typography>
      </Paper>

      <DataTable<Segment>
        columns={columns}
        rows={paged}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        total={sorted.length}
        orderBy={orderBy}
        order={order}
        onPageChange={(p) => setPage(p)}
        onRowsPerPageChange={(r) => { setRowsPerPage(r); setPage(0); }}
        onRequestSort={(col) => {
          if (orderBy === col) {
            setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
          } else {
            setOrderBy(col);
            setOrder('asc');
          }
        }}
      />
    </Box>
  );
}

function SegmentForm({ isEdit }: { isEdit?: boolean }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const params = useParams();
  const segmentId = params.segmentId as string | undefined;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!isEdit);
  const [form, setForm] = useState<SegmentRQ>({ name: '', description: undefined, enableInFollowUp: false });

  useEffect(() => {
    const load = async () => {
      if (!isEdit || !segmentId) {
        setInitialLoading(false);
        return;
      }
      try {
        const rs = await segmentApi.get(segmentId);
        setForm({ name: rs.name, description: rs.description ?? undefined, enableInFollowUp: rs.enableInFollowUp ?? false });
      } catch (e: any) {
        toast.error(e?.message || t('segments.errors.loadSegmentError'));
      } finally {
        setInitialLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, segmentId]);

  const canSubmit = useMemo(() => !!form.name?.trim(), [form.name]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isEdit && segmentId) {
        const result = await segmentApi.update(segmentId, form);
        if (result.success) {
          toast.success(t('segments.notifications.updated'));
          navigate('..');
        } else {
          toast.error(result.error || t('segments.errors.updateError'));
        }
      } else {
        const result = await segmentApi.create(form);
        if (result.success) {
          toast.success(t('segments.notifications.created'));
          navigate('..');
        } else {
          toast.error(result.error || t('segments.errors.createError'));
        }
      }
    } catch (e: any) {
      toast.error(t('segments.errors.unexpectedError'));
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('..')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {isEdit ? t('segments.edit') : t('segments.newSegment')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        }}
      >
        <Box>
          <TextField
            fullWidth
            label={t('segments.name')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Box>
        <Box>
          <TextField
            fullWidth
            label={t('segments.description')}
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value || undefined })}
          />
        </Box>
        <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.enableInFollowUp ?? false}
                onChange={(e) => setForm({ ...form, enableInFollowUp: e.target.checked })}
                color="primary"
              />
            }
            label={t('segments.enableInFollowUp')}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={() => navigate('..')}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          disabled={!canSubmit || loading}
          onClick={handleSubmit}
        >
          {loading ? t('segments.saving') : t('segments.save')}
        </Button>
      </Box>
    </Paper>
  );
}

const SegmentsManagement: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<SegmentsList />} />
      <Route path="/create" element={<SegmentForm />} />
      <Route path=":segmentId/edit" element={<SegmentForm isEdit />} />
    </Routes>
  );
};

export default SegmentsManagement;
